import KitNeedleHookUpsell from '@/components/KitNeedleHookUpsell/KitNeedleHookUpsell.vue';
import { mountCoreVueApp } from '@/entrypoints/helpers/core-vue-app';
import { initTabCounters } from '@/entrypoints/helpers/product-tab-async.ts';
import { entityTypes } from '@/shared/constants/constants';
import { initHobbiiStarter } from '@/shared/instances/hobbii-starter.ts';
import { initKitCartActions } from '@/shared/instances/kit-cart-button';
import { initQuantitySelector } from '@/shared/instances/quantity-selector.js';
// Wishlist is not available for kits: WishlistButton requires either a SKU from
// the options store (kits have no variant picker) or a productGroupId, neither
// of which is available on kit pages.
import { useProductStore } from '@/shared/pinia/modules/product';
import { trackProductViewed } from '@/shared/tracking/product-viewed.ts';
import { getProductDataFromHTML } from '@/shared/utils/product';

import { initProductInfoTabs } from './helpers/product-info-tabs';
import { setReadMoreListeners } from './helpers/read-more';

const quantityPickerMountId = 'js-product-quantity-selector';

// FlyBundles sets data-show-native-button="false" on this section when it renders its own ATC button
const FLY_BUNDLES_ATC_SECTION_SELECTOR =
  '.gfbFrequentBoughtAddToCartButtonSection';

const isFlyBundlesOwnButtonVisible = () => {
  const section = document.querySelector(FLY_BUNDLES_ATC_SECTION_SELECTOR);
  return section?.getAttribute('data-show-native-button') === 'false';
};

const showHobbiiAtc = () => {
  const hobbiiAtc = document.getElementById('hobbii-kit-atc');
  if (!hobbiiAtc) return;

  if (!window.gfb) return;

  const offerId = window.gfb.gfbFrequentBought.state.activeOffer[0];
  const offerData = offerId
    ? window.gfb.gfbFrequentBought.state.frequentBoughtDataByOfferId[offerId]
    : undefined;

  if (!offerData?.advancedConfiguration.useThemeATCButton) return;
  if (isFlyBundlesOwnButtonVisible()) return;

  hobbiiAtc.classList.remove('hidden');
};

// FlyBundles loads asynchronously — gfb and activeOffer may not be populated
// when our entrypoint runs. Poll until ready, then show the button.
const showHobbiiAtcWhenReady = () => {
  const MAX_WAIT_MS = 5000;
  const INTERVAL_MS = 100;
  let elapsed = 0;

  const interval = setInterval(() => {
    elapsed += INTERVAL_MS;

    const gfbState = window.gfb?.gfbFrequentBought?.state;
    const offerId = gfbState?.activeOffer[0];
    if (offerId && gfbState?.frequentBoughtDataByOfferId[offerId]) {
      clearInterval(interval);
      showHobbiiAtc();
      return;
    }

    if (elapsed >= MAX_WAIT_MS) {
      clearInterval(interval);
    }
  }, INTERVAL_MS);
};

const showKitFallbackDescription = () => {
  document.getElementById('js-kit-description-skeleton')?.remove();
  document
    .getElementById('js-kit-description-content')
    ?.classList.remove('hidden');
};

const injectRelatedPatternDetails = async (handle: string) => {
  const container = document.getElementById('js-related-pattern-details');
  const description = document.getElementById('js-kit-description');
  if (!container) return;

  let response: Response;
  try {
    response = await fetch(`/products/${handle}?view=pattern-details`);
    if (!response.ok) throw new Error();
  } catch {
    container.remove();
    showKitFallbackDescription();
    return;
  }

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const patternDetails = doc.querySelector('[name="pattern-details"]');

  if (patternDetails) {
    container.outerHTML = patternDetails.outerHTML;
  } else {
    container.remove();
  }

  const patternDescription = doc.querySelector('[name="product-description"]');
  if (description) {
    if (patternDescription) {
      description.innerHTML = patternDescription.outerHTML;
      setReadMoreListeners();
    } else {
      showKitFallbackDescription();
    }
  }
};

const initProductKit = async () => {
  await initHobbiiStarter();

  const { productId, productName } = getProductDataFromHTML();

  const productStore = useProductStore();
  productStore.entityType = entityTypes.KIT;
  if (productId) productStore.productId = productId;
  if (productName) productStore.productName = productName;
  await productStore.setRelatedPatternFromKit();

  const relatedPatternHandle = productStore.relatedPattern?.handle;
  if (relatedPatternHandle) {
    injectRelatedPatternDetails(relatedPatternHandle);
  } else {
    document.getElementById('js-related-pattern-details')?.remove();
    showKitFallbackDescription();
  }

  const upsellMount = document.getElementById('js-kit-needle-hook-upsell');
  if (upsellMount) {
    mountCoreVueApp(KitNeedleHookUpsell, upsellMount);
  }

  showHobbiiAtcWhenReady();
  initQuantitySelector(quantityPickerMountId);
  void initKitCartActions('js-product-cart-actions');
  setReadMoreListeners();
  void initProductInfoTabs();
  initTabCounters();
  trackProductViewed(true);
};

initProductKit();
